/* Globo filter drawer
 *
 * Globo renders the drawer itself and re-renders parts of it on every filter change, diffing its own markup
 * back in. Anything added here would be wiped on the next change, so a MutationObserver puts it back rather
 * than a one-off edit. Every write checks first, so the observer never triggers itself in a loop.
 */
(() => {
  const labels = window.opGloboFilterLabels || {};
  const root = document.documentElement;

  const globoActive = () =>
    root.classList.contains('spf-has-filter') && typeof window.globofilter?.toggleCanvas === 'function';

  /* The toolbar's Filter button opens Prestige's drawer through a delegated click on aria-controls. Catching
   * the click in the capture phase lets Globo's drawer open instead, and leaves the native drawer as the
   * fallback whenever Globo has not loaded. */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[aria-controls="facets-drawer"]');

    if (!button || !globoActive()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    window.globofilter.toggleCanvas();
  }, true);

  /* Sort by. Prestige's popover asks the facets code to re-render the section with a new sort_by, which would
   * replace Globo's grid with Shopify's unfiltered one. With Globo active the sort goes to Globo instead, the
   * way its own toolbar's changeSortBy does it — that function is not called directly because it assumes
   * Globo's toolbar markup, which is hidden. The popover still closes and marks the choice itself. */
  document.addEventListener('facet:update', (event) => {
    if (!event.target.closest?.('facets-sort-popover') || !globoActive()) {
      return;
    }

    const sortBy = event.detail?.url?.searchParams?.get('sort_by');

    event.stopImmediatePropagation();

    if (!sortBy || window.globofilter.state?.queryParams?.sort_by === sortBy) {
      return;
    }

    window.globofilter.state.queryParams.sort_by = sortBy;
    delete window.globofilter.state.queryParams.page;
    window.globofilter.getFilterData('products', true);
  }, true);

  /* The popover's options are Shopify's sort orders, printed by the section — Featured, A-Z and so on — while
   * Globo sorts by its own list, set up in the app: Latest, Price, Best Selling, Biggest Discount. Globo prints
   * that list in its hidden toolbar (#globo-dropdown-sort_options), so whenever Sort by opens with Globo
   * active, the popover's list is rebuilt from it, with Globo's current order marked. <x-listbox> binds its
   * options once, when it connects, so the whole element is replaced rather than its buttons swapped. Where
   * Globo prints no list the section's own options stay. */
  const currentGloboSort = (options) => {
    const sortBy = window.globofilter?.state?.queryParams?.sort_by;

    if (sortBy) {
      return sortBy;
    }

    const label = document.querySelector('#sortButton')?.textContent.trim();
    const match = options.find((option) => option.getAttribute('aria-label') === label);

    return (match || options[0]).dataset.sort;
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[aria-controls="sort-by-popover"]');

    if (!button || !globoActive()) {
      return;
    }

    const popover = document.getElementById('sort-by-popover');
    const listbox = popover?.querySelector('x-listbox');
    const options = [...document.querySelectorAll('#globo-dropdown-sort_options [data-sort]')];

    if (!listbox || options.length === 0) {
      return;
    }

    const selected = currentGloboSort(options);
    const template = listbox.querySelector('[role="option"]');
    const replacement = document.createElement('x-listbox');

    replacement.className = listbox.className;

    options.forEach((option) => {
      const item = document.createElement('button');
      const label = document.createElement('span');

      item.type = 'button';
      item.className = template?.className || 'popover__value-option group';
      item.setAttribute('role', 'option');
      item.value = option.dataset.sort;
      item.setAttribute('aria-selected', String(option.dataset.sort === selected));

      label.className = template?.firstElementChild?.className || 'reversed-link';
      label.textContent = option.getAttribute('aria-label') || option.textContent.trim();

      item.append(label);
      replacement.append(item);
    });

    listbox.replaceWith(replacement);
  }, true);

  /* The tiling switch finds the product list by the id in its aria-controls. Globo renames that list to
   * #gf-products and keeps the original in an old-id attribute, so the switch is pointed at whatever the list
   * is called at the moment it is used — which also covers Globo handing the old id back. */
  document.addEventListener('click', (event) => {
    const layoutSwitch = event.target.closest('collection-layout-switch');
    const controls = layoutSwitch?.getAttribute('aria-controls');

    if (!controls || document.getElementById(controls)) {
      return;
    }

    const list = document.querySelector(`[old-id="${CSS.escape(controls)}"]`);

    if (list?.id) {
      layoutSwitch.setAttribute('aria-controls', list.id);
    }
  }, true);

  const setText = (element, text) => {
    if (element && text && element.textContent !== text) {
      element.textContent = text;
    }
  };

  /* The design's footer is two buttons, Re-Set and Apply. Globo draws one, "View N products", which closes
   * the drawer — filters apply as they are picked, so that is Apply in all but name. Re-Set is added beside
   * it and clears everything without closing. */
  const dress = (tree) => {
    setText(tree.querySelector('.gf-filter-heading'), labels.heading);

    const footer = tree.querySelector('.gf-filter-footer');

    if (!footer) {
      return;
    }

    const apply = footer.querySelector('button:not(.op-gf-reset)');

    if (apply) {
      apply.classList.add('op-gf-apply');
      setText(apply, labels.apply);
    }

    /* Appended rather than prepended, and drawn first by CSS order: Globo diffs the footer child by child, so
     * Globo's own button has to stay first or the diff would morph this one into it. */
    if (!footer.querySelector('.op-gf-reset')) {
      const reset = document.createElement('button');

      reset.type = 'button';
      reset.className = 'op-gf-reset';
      reset.textContent = labels.reset || 'Re-Set';
      footer.append(reset);
    }
  };

  /* Delegated for the same reason — a listener on the element would survive being morphed into something
   * else, where a class check does not. */
  document.addEventListener('click', (event) => {
    if (event.target.closest('#gf-tree .op-gf-reset')) {
      window.clearAllFilter?.();
    }
  });

  let treeObserver = null;

  const watchTree = () => {
    const tree = document.getElementById('gf-tree');

    if (!tree || treeObserver?.tree === tree) {
      return;
    }

    treeObserver?.disconnect();
    treeObserver = new MutationObserver(() => dress(tree));
    treeObserver.tree = tree;
    treeObserver.observe(tree, { childList: true, subtree: true, characterData: true });
    dress(tree);
  };

  /* Globo appends #gf-tree to <body> once its data arrives, and can remove and rebuild it. */
  new MutationObserver(watchTree).observe(document.body, { childList: true });
  window.addEventListener('globoFilterRenderCompleted', watchTree);
  watchTree();

  /* The grid's watchdog.
   *
   * Globo is told to keep the server-rendered grid on a first load (snippets/globo-filter.liquid), so the
   * only load the theme hides the grid on is the one Globo still redraws: a URL arriving with its filter,
   * form or search parameters already on it. The snippet marks that with .op-gf-deferred-grid and
   * globo-filter.css holds the grid back while Globo's own `spf-filter-loading` sits on <html>.
   *
   * This is the watchdog for the one case Globo cannot cover on that load: its script never running at
   * all — blocked, or dead on an error — which would leave the class on and the grid hidden for good. An
   * empty grid is far worse than the wrong products, so after four seconds the theme reveals it whatever
   * Globo is doing. Globo's own class is left alone; the reveal is a separate class, so nothing here
   * confuses the app. Armed only where the grid is actually hidden, so an ordinary collection page sets
   * no timer. */
  if (root.classList.contains('op-gf-deferred-grid') && root.classList.contains('spf-filter-loading')) {
    setTimeout(() => root.classList.add('op-gf-reveal'), 4000);
  }
})();